import { type } from 'arktype'

import { TechnicalError } from '@orus.eu/error'

import { logger } from '../../lib/logger.ts'

/** Reaching this many identical bounces in a row with no authorized navigation in between is reported as a loop */
const LOOP_BOUNCE_THRESHOLD = 3

const STORAGE_KEY = 'orus.login-bounce'

const bounceStateSchema = type({ loginHref: 'string', count: 'number' })

/**
 * Records a bounce to the login page and reports a redirect loop to Sentry once the same login href has
 * been produced {@link LOOP_BOUNCE_THRESHOLD} times in a row without an authorized navigation in between
 *
 * {@link buildLoginHref} is idempotent, so the redirect URL no longer grows on each hop
 * {@link resetLoginBounce} clears the count as soon as the user lands authorized or logs in interactively,
 * so legitimate re-logins never accumulate toward the threshold
 */
export function recordLoginBounce(loginHref: string): void {
  const previous = readBounceState()
  const count = previous?.loginHref === loginHref ? previous.count + 1 : 1

  writeBounceState({ loginHref, count })

  if (count >= LOOP_BOUNCE_THRESHOLD) {
    logger.error(
      new TechnicalError('Login redirect loop detected', {
        context: { loginHref, count, currentHref: window.location.href },
      }),
    )
  }
}

/** Clears the bounce counter. Call when the user reaches an authorized page or logs in interactively. */
export function resetLoginBounce(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode / disabled): nothing to clear
  }
}

function readBounceState(): typeof bounceStateSchema.infer | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = bounceStateSchema(JSON.parse(raw))
    return parsed instanceof type.errors ? null : parsed
  } catch {
    return null
  }
}

function writeBounceState(state: typeof bounceStateSchema.infer): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable (private mode / disabled): detection degrades to best-effort, never blocks navigation
  }
}
